/**
 * Oblikovanje za prikaz, enako kot na strežniku (PDF) in v Swingu: slovenski zapis, minus kot »-«
 * (Intl za sl uporablja »−«, ki ga strežnik namenoma ne).
 */
// useGrouping 'always': CLDR za sl štirimestnih števil ne loči (1136,50), Swing in PDF pa jih (1.136,50)
const denarOblika = new Intl.NumberFormat('sl-SI', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: 'always',
});

export function denar(znesek: number | undefined | null): string {
  if (znesek === undefined || znesek === null) {
    return '';
  }
  return denarOblika.format(znesek).replace('−', '-');
}

// največ 10 decimalk: poraba umetne inteligence ima do 7, več bi pokazalo napako zapisa double (0,1 → 0,1000000000000000055)
const tockeOblika = new Intl.NumberFormat('sl-SI', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 10,
  useGrouping: 'always',
});

/**
 * Točke (stanje in gibanja) natančno, kot so na strežniku: vsaj dve decimalki, več samo, kjer so (poraba umetne
 * inteligence se zaračuna brez zaokroževanja na cent: »1,9993229«, »-0,0002257«), da ni videti kot 2,00.
 */
export function tocke(n: number | undefined | null): string {
  if (n === undefined || n === null) {
    return '';
  }
  return tockeOblika.format(n).replace('\u2212', '-');
}

const steviloOblika = new Intl.NumberFormat('sl-SI', { maximumFractionDigits: 0, useGrouping: 'always' });

/** Celo število z ločilom tisočic (2.336). */
export function stevilo(n: number): string {
  return steviloOblika.format(n).replace('\u2212', '-');
}

const odstotekOblika = new Intl.NumberFormat('sl-SI', { maximumFractionDigits: 2, useGrouping: 'always' });

/** Odstotek brez znaka % (22, 9,5, 24,4). */
export function odstotek(n: number): string {
  return odstotekOblika.format(n).replace('\u2212', '-');
}

const kolicinaOblika = new Intl.NumberFormat('sl-SI', { maximumFractionDigits: 3, useGrouping: 'always' });

/** Količina postavke (do tri decimalke: 1,5, 0,125, 1.200). */
export function kolicina(n: number | undefined | null): string {
  if (n === undefined || n === null) {
    return '';
  }
  return kolicinaOblika.format(n).replace('\u2212', '-');
}

const sekundeOblika = new Intl.NumberFormat('sl-SI', { maximumFractionDigits: 1, useGrouping: 'always' });

/** Trajanje v ms kot sekunde na eno decimalko (»1,3 s«). */
export function trajanje(ms: number | undefined | null): string {
  return `${sekundeOblika.format((ms ?? 0) / 1000)} s`;
}

/** »2026-10-07« (datum iz API-ja) → »7. 10. 2026«. */
export function datum(iso: string | undefined | null): string {
  if (!iso) {
    return '';
  }
  const [leto, mesec, dan] = iso.split('-');
  if (!leto || !mesec || !dan) {
    return iso;
  }
  return `${Number(dan)}. ${Number(mesec)}. ${leto}`;
}

/** Datum iz izbirnika (Mantine vrne »YYYY-MM-DD«) ali prazno → parameter za API. */
export function datumZaApi(vrednost: string | null | undefined): string | undefined {
  return vrednost ? vrednost.slice(0, 10) : undefined;
}

const meseci = [
  'januar',
  'februar',
  'marec',
  'april',
  'maj',
  'junij',
  'julij',
  'avgust',
  'september',
  'oktober',
  'november',
  'december',
];

/** Mesec iz analiz za os grafa: »2026-05« → »maj«, »2026-10« → »okt«. */
export function mesecKratko(interval: string | undefined | null): string {
  const mesec = Number(interval?.split('-')[1]);
  const ime = meseci[mesec - 1];
  return !ime ? (interval ?? '') : ime.length <= 4 ? ime : ime.slice(0, 3);
}

/** Znesek v celih evrih za pregled (»52.300 €«, »-1.200 €«). */
export function evri(znesek: number | undefined | null): string {
  if (znesek === undefined || znesek === null) {
    return '';
  }
  return `${stevilo(Math.round(znesek))} €`;
}

/** Obdobje iz analiz: »2026« → »2026«, »2026-05« → »maj 2026«, »2026-05-20« → »20. 5. 2026«. */
export function obdobje(interval: string | undefined | null): string {
  if (!interval) {
    return '';
  }
  const [leto, mesec, dan] = interval.split('-');
  if (dan) {
    return datum(interval);
  }
  if (mesec) {
    return `${meseci[Number(mesec) - 1] ?? mesec} ${leto}`;
  }
  return interval;
}

/** Datum, shranjen kot epoch ms (npr. dodatni stroški), → »2026-10-07« v lokalnem času (za {@link datum} in izbirnik). */
export function datumIzMs(ms: number | undefined | null): string {
  if (!ms) {
    return '';
  }
  const d = new Date(ms);
  const dd = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dd(d.getMonth() + 1)}-${dd(d.getDate())}`;
}

/** »2026-10-07« (izbirnik) → epoch ms lokalne polnoči, kot je datum shranjeval Swing. */
export function msIzDatuma(iso: string): number {
  const [leto, mesec, dan] = iso.slice(0, 10).split('-').map(Number);
  return new Date(leto!, (mesec ?? 1) - 1, dan ?? 1).getTime();
}

/** Cena v oglasu: cela brez decimalk (»14.990 €«), sicer z dvema (»14.990,50 €«). */
export function cenaOglasa(znesek: number | undefined | null): string {
  if (znesek === undefined || znesek === null) {
    return '';
  }
  return `${Number.isInteger(znesek) ? stevilo(znesek) : denar(znesek)} €`;
}

/** Čas iz ms: »7. 10. 2026 ob 14.05«. */
export function casIzMs(ms: number | undefined | null): string {
  if (ms === undefined || ms === null) {
    return '';
  }
  const d = new Date(ms);
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ob ${d.getHours()}.${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Koliko časa je minilo (za sezname na začetnem zaslonu): »pravkar«, »pred 5 min«, »pred 1 uro«, »pred 2 urama«,
 * »pred 3 urami«, »včeraj«, »pred 2 dnevoma«, »pred 5 dnevi«; od 7 dni naprej datum.
 */
export function preteklo(ms: number | undefined | null, zdaj = Date.now()): string {
  if (ms === undefined || ms === null) {
    return '';
  }
  const minut = Math.floor((zdaj - ms) / 60_000);
  if (minut < 1) {
    return 'pravkar';
  }
  if (minut < 60) {
    return `pred ${minut} min`;
  }
  const ur = Math.floor(minut / 60);
  if (ur < 24) {
    return `pred ${ur} ${ur === 1 ? 'uro' : ur === 2 ? 'urama' : 'urami'}`;
  }
  const dni = Math.floor(ur / 24);
  if (dni === 1) {
    return 'včeraj';
  }
  if (dni < 7) {
    return `pred ${dni} ${dni === 2 ? 'dnevoma' : 'dnevi'}`;
  }
  return datumIzMs(ms).split('-').reverse().map(Number).join('. ');
}

/**
 * Število s samostalnikom v pravi obliki (slovenska dvojina in množina): {@code oblike} = [1, 2, 3–4, 5+], npr.
 * {@code koliko(2, ['vozilo', 'vozili', 'vozila', 'vozil'])} → »2 vozili«.
 */
export function koliko(n: number, oblike: [string, string, string, string]): string {
  const s = n % 100;
  return `${stevilo(n)} ${oblike[s === 1 ? 0 : s === 2 ? 1 : s === 3 || s === 4 ? 2 : 3]}`;
}
